import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AppState, Session, Message, Action, CreateActionRequest, UpdateActionRequest } from '@/types';
import { apiClient } from '@/services/api';

interface AppStore extends AppState {
  // User Actions
  setUser: (user: Partial<AppState['user']>) => void;
  
  // Session Actions
  setSessions: (sessions: Session[]) => void;
  setCurrentSession: (session: Session | null) => void;
  setSessionsLoading: (loading: boolean) => void;
  setSessionsError: (error: string | null) => void;
  setWelcomeState: (isInWelcomeState: boolean) => void;
  clearWelcomeState: () => void;
  
  // Chat Actions
  addMessage: (message: Message) => void;
  setMessages: (messages: Message[]) => void;
  updateMessageFeedback: (messageId: string, feedback: { type: 'like' | 'dislike' | null; timestamp?: Date }) => void;
  setStreaming: (streaming: boolean) => void;
  setTyping: (typing: boolean) => void;
  setChatError: (error: string | null) => void;
  
  // Action Management Actions
  fetchActions: () => Promise<void>;
  createAction: (request: CreateActionRequest) => Promise<Action>;
  updateAction: (actionId: string, request: UpdateActionRequest) => Promise<Action>;
  deleteAction: (actionId: string) => Promise<void>;
  triggerAction: (actionId: string) => Promise<void>;
  setSelectedAction: (action: Action | null) => void;
  setActionsLoading: (loading: boolean) => void;
  setActionsError: (error: string | null) => void;
  clearActionsError: () => void;
  fetchCorpusFiles: () => Promise<void>;
  setCorpusFilesLoading: (loading: boolean) => void;
  
  // UI Actions
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setMobile: (mobile: boolean) => void;
}

export const useAppStore = create<AppStore>()(
  devtools(
    (set) => ({
      // Initial state
      user: {
        id: '',
        name: '',
        authenticated: false,
      },
      sessions: {
        list: [],
        current: null,
        loading: false,
        error: null,
        isInWelcomeState: true,
      },
      chat: {
        messages: [],
        streaming: false,
        typing: false,
        error: null,
      },
      actions: {
        list: [],
        selectedAction: null,
        loading: false,
        error: null,
        corpusFiles: [],
        corpusFilesLoading: false,
      },
      ui: {
        sidebarOpen: true,
        theme: 'light',
        mobile: false,
      },

      // Actions
      setUser: (user) =>
        set((state) => ({
          user: { ...state.user, ...user },
        })),

      setSessions: (sessions) =>
        set((state) => ({
          sessions: { ...state.sessions, list: sessions },
        })),

      setCurrentSession: (session) =>
        set((state) => ({
          sessions: { 
            ...state.sessions, 
            current: session,
            isInWelcomeState: session === null
          },
        })),

      setSessionsLoading: (loading) =>
        set((state) => ({
          sessions: { ...state.sessions, loading },
        })),

      setSessionsError: (error) =>
        set((state) => ({
          sessions: { ...state.sessions, error },
        })),

      setWelcomeState: (isInWelcomeState) =>
        set((state) => ({
          sessions: { ...state.sessions, isInWelcomeState },
        })),

      clearWelcomeState: () =>
        set((state) => ({
          sessions: { ...state.sessions, isInWelcomeState: false },
        })),

      addMessage: (message) =>
        set((state) => ({
          chat: {
            ...state.chat,
            messages: [...state.chat.messages, message],
          },
        })),

      setMessages: (messages) =>
        set((state) => ({
          chat: { ...state.chat, messages },
        })),

      setStreaming: (streaming) =>
        set((state) => ({
          chat: { ...state.chat, streaming },
        })),

      setTyping: (typing) =>
        set((state) => ({
          chat: { ...state.chat, typing },
        })),

      setChatError: (error) =>
        set((state) => ({
          chat: { ...state.chat, error },
        })),

      toggleSidebar: () =>
        set((state) => ({
          ui: { ...state.ui, sidebarOpen: !state.ui.sidebarOpen },
        })),

      setTheme: (theme) =>
        set((state) => ({
          ui: { ...state.ui, theme },
        })),

      setMobile: (mobile) =>
        set((state) => ({
          ui: { ...state.ui, mobile },
        })),

      updateMessageFeedback: (messageId, feedback) =>
        set((state) => ({
          chat: {
            ...state.chat,
            messages: state.chat.messages.map((message) =>
              message.id === messageId
                ? {
                    ...message,
                    feedback: {
                      eventId: messageId,
                      type: feedback.type,
                      timestamp: feedback.timestamp || new Date(),
                    },
                  }
                : message
            ),
          },
        })),

      // Action Management Actions
      fetchActions: async () => {
        set((state) => ({
          actions: { ...state.actions, loading: true, error: null },
        }));

        try {
          const actions = await apiClient.getActions();
          set((state) => ({
            actions: { ...state.actions, list: actions, loading: false },
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch actions';
          set((state) => ({
            actions: { ...state.actions, loading: false, error: errorMessage },
          }));
          throw error;
        }
      },

      createAction: async (request: CreateActionRequest) => {
        set((state) => ({
          actions: { ...state.actions, loading: true, error: null },
        }));

        try {
          const newAction = await apiClient.createAction(request);
          set((state) => ({
            actions: {
              ...state.actions,
              list: [...state.actions.list, newAction],
              loading: false,
            },
          }));
          return newAction;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to create action';
          set((state) => ({
            actions: { ...state.actions, loading: false, error: errorMessage },
          }));
          throw error;
        }
      },

      updateAction: async (actionId: string, request: UpdateActionRequest) => {
        set((state) => ({
          actions: { ...state.actions, loading: true, error: null },
        }));

        try {
          const updatedAction = await apiClient.updateAction(actionId, request);
          set((state) => ({
            actions: {
              ...state.actions,
              list: state.actions.list.map((action) =>
                action.id === actionId ? updatedAction : action
              ),
              selectedAction: state.actions.selectedAction?.id === actionId 
                ? updatedAction 
                : state.actions.selectedAction,
              loading: false,
            },
          }));
          return updatedAction;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update action';
          set((state) => ({
            actions: { ...state.actions, loading: false, error: errorMessage },
          }));
          throw error;
        }
      },

      deleteAction: async (actionId: string) => {
        set((state) => ({
          actions: { ...state.actions, loading: true, error: null },
        }));

        try {
          await apiClient.deleteAction(actionId);
          set((state) => ({
            actions: {
              ...state.actions,
              list: state.actions.list.filter((action) => action.id !== actionId),
              selectedAction: state.actions.selectedAction?.id === actionId 
                ? null 
                : state.actions.selectedAction,
              loading: false,
            },
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete action';
          set((state) => ({
            actions: { ...state.actions, loading: false, error: errorMessage },
          }));
          throw error;
        }
      },

      triggerAction: async (actionId: string) => {
        try {
          await apiClient.triggerAction(actionId);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to trigger action';
          set((state) => ({
            actions: { ...state.actions, error: errorMessage },
          }));
          throw error;
        }
      },

      setSelectedAction: (action) =>
        set((state) => ({
          actions: { ...state.actions, selectedAction: action },
        })),

      setActionsLoading: (loading) =>
        set((state) => ({
          actions: { ...state.actions, loading },
        })),

      setActionsError: (error) =>
        set((state) => ({
          actions: { ...state.actions, error },
        })),

      clearActionsError: () =>
        set((state) => ({
          actions: { ...state.actions, error: null },
        })),

      fetchCorpusFiles: async () => {
        set((state) => ({
          actions: { ...state.actions, corpusFilesLoading: true },
        }));

        try {
          const corpusFiles = await apiClient.getCorpusFiles();
          set((state) => ({
            actions: { 
              ...state.actions, 
              corpusFiles, 
              corpusFilesLoading: false 
            },
          }));
        } catch (error) {
          console.error('Failed to fetch corpus files:', error);
          set((state) => ({
            actions: { 
              ...state.actions, 
              corpusFiles: [], 
              corpusFilesLoading: false 
            },
          }));
          // Don't throw error for corpus files as it's not critical
        }
      },

      setCorpusFilesLoading: (loading) =>
        set((state) => ({
          actions: { ...state.actions, corpusFilesLoading: loading },
        })),
    }),
    {
      name: 'app-store',
    }
  )
);